import * as React from 'react';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { IStringLocalizer } from '../../../utilities/IStringLocalizer';
import { AITerminateModal } from './AITerminateModal';
import {
    actionCreators as aiActionCreators
} from '../../../store/StateControllers/Survey/SurveyResponse/AISurveyAgentController';
import { useLocalizer } from './hooks/useLocalizer';
import { useAiActionTarget } from './hooks/useAiActionTarget';
import { hidePopoverAndBlur, useBootstrapPopover } from './hooks/useBootstrapPopover';
import { showBootstrapModal } from './actionButtonUtils';
import { DataCollectionAiAgentOperationType } from '../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseModels';

interface AITerminateButtonProps {
    onError?: (message: string) => void;
    localizer?: IStringLocalizer;
    operationType?: DataCollectionAiAgentOperationType | null;
}

export const AITerminateButton: React.FC<AITerminateButtonProps> = ({
    onError,
    localizer,
    operationType
}) => {
    const dispatch = useDispatch();
    const localize = useLocalizer(localizer);
    const [isTerminateModalOpen, setIsTerminateModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { requestId, operationId } = useAiActionTarget(operationType);
    const buttonRef = React.useRef<HTMLButtonElement | null>(null);
    const isMountedRef = React.useRef(true);

    useBootstrapPopover(buttonRef);

    React.useEffect(() => {
        isMountedRef.current = true;

        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const safeSetTerminateModalOpen = (isOpen: boolean) => {
        if (isMountedRef.current) {
            setIsTerminateModalOpen(isOpen);
        }
    };

    const safeSetSubmitting = (isBusy: boolean) => {
        if (isMountedRef.current) {
            setIsSubmitting(isBusy);
        }
    };

    const showTerminateModal = () => {
        hidePopoverAndBlur(buttonRef);
        safeSetTerminateModalOpen(true);
        showBootstrapModal('ai-terminate-modal');
    };

    const handleTerminateCancel = () => {
        if (isSubmitting) {
            return;
        }

        safeSetTerminateModalOpen(false);
    };

    const handleTerminateConfirm = async () => {
        if (!requestId || requestId.trim() === '') {
            onError?.(localize('Invalid request. Please refresh the page and try again.'));
            return;
        }

        if (!operationId || operationId.trim() === '') {
            onError?.(localize('No AI operation found to terminate. Please refresh the page and try again.'));
            return;
        }

        safeSetSubmitting(true);

        try {
            const result = await dispatch(
                aiActionCreators.terminateAIWorkflow(requestId, operationId) as any
            );

            if (!result?.success) {
                onError?.(localize('Failed to terminate AI workflow. Please try again.'));
                return;
            }

            safeSetTerminateModalOpen(false);
        } catch {
            onError?.(localize('Failed to terminate AI workflow. Please try again.'));
        } finally {
            safeSetSubmitting(false);
        }
    };

    return (
        <>
            <button
                ref={buttonRef}
                type="button"
                onClick={showTerminateModal}
                className="btn ai-terminate-button"
                disabled={isSubmitting}
                data-toggle="popover"
                data-container="body"
                data-placement="bottom"
                data-content={localize('Terminate this in-progress AI request. No AI answers will be applied to the survey.')}
            >
                {localize('Terminate Request')}
            </button>
            <AITerminateModal
                isOpen={isTerminateModalOpen}
                isSubmitting={isSubmitting}
                onConfirm={handleTerminateConfirm}
                onCancel={handleTerminateCancel}
                localizer={localizer}
            />
        </>
    );
};

export default AITerminateButton;
